import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarketingStrategistRendererProps {
  data: any;
}

const MarketingStrategistRenderer: React.FC<MarketingStrategistRendererProps> = ({ data }) => {
  if (!data.hero && !data.sections && !data.site_architecture) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="text-sm text-gray-500">No content available</div>
      </div>
    );
  }

  const pageType = data.page_type || 'general';
  const isBlogPost = pageType === 'blog_post';
  const isPositioningDoc = pageType === 'positioning_document';
  const isWebsiteStructure = pageType === 'website_structure';
  const isLandingPage = pageType === 'landing_page';

  // Positioning Document Rendering
  if (isPositioningDoc) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Page Type Badge */}
        <div className="bg-indigo-100 px-4 py-2 border-b border-indigo-200">
          <span className="text-xs font-semibold text-indigo-800 uppercase tracking-wide">
            📋 Positioning Document
          </span>
        </div>

        <div className="p-6 space-y-6">
          {/* Document Title */}
          {data.hero && (
            <div className="border-b border-gray-200 pb-4">
              <h1 className="text-3xl font-bold text-gray-900 leading-tight">{data.hero.h1}</h1>
            </div>
          )}

          {/* Strategic Content with Markdown Rendering */}
          <article className="prose prose-lg max-w-none prose-headings:font-bold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900">
            {data.sections?.map((section: any, sectionIndex: number) => (
              <div key={sectionIndex} className="mb-8">
                {section.heading && (
                  <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4 border-b border-gray-200 pb-2">
                    {section.heading}
                  </h2>
                )}
                {section.subsections && section.subsections.length > 0 && (
                  <div className="space-y-6">
                    {section.subsections.map((subsection: any, subIndex: number) => (
                      <div key={subIndex}>
                        {subsection.subheading && (
                          <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">
                            {subsection.subheading}
                          </h3>
                        )}
                        <div className="text-gray-700 leading-relaxed text-base">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {subsection.content}
                          </ReactMarkdown>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </article>
        </div>
      </div>
    );
  }

  // Website Structure Rendering
  if (isWebsiteStructure && data.site_architecture) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Page Type Badge */}
        <div className="bg-teal-100 px-4 py-2 border-b border-teal-200">
          <span className="text-xs font-semibold text-teal-800 uppercase tracking-wide">
            🏗️ Website Structure
          </span>
        </div>

        <div className="p-6 space-y-8">
          {/* Title */}
          {data.hero && (
            <div className="border-b border-gray-200 pb-4">
              <h1 className="text-2xl font-bold text-gray-900">{data.hero.h1}</h1>
            </div>
          )}

          {/* Phase 1: Positioning Inputs */}
          {data.site_architecture.positioning_inputs && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3">
              <h2 className="text-lg font-bold text-indigo-900">Phase 1: Positioning Inputs</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="font-medium text-indigo-900">Primary ICP:</span>
                  <span className="text-indigo-800 ml-2">
                    {data.site_architecture.positioning_inputs.primary_icp}
                  </span>
                </div>
                {data.site_architecture.positioning_inputs.secondary_icps?.length > 0 && (
                  <div>
                    <span className="font-medium text-indigo-900">Secondary ICPs:</span>
                    <span className="text-indigo-800 ml-2">
                      {data.site_architecture.positioning_inputs.secondary_icps.join(', ')}
                    </span>
                  </div>
                )}
                <div>
                  <span className="font-medium text-indigo-900">Job to be Done:</span>
                  <span className="text-indigo-800 ml-2">
                    {data.site_architecture.positioning_inputs.job_to_be_done}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-indigo-900">Core Pain:</span>
                  <span className="text-indigo-800 ml-2">
                    {data.site_architecture.positioning_inputs.core_pain}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-indigo-900">Category to Own:</span>
                  <span className="text-indigo-800 ml-2">
                    {data.site_architecture.positioning_inputs.category_to_own}
                  </span>
                </div>
                {data.site_architecture.positioning_inputs.alternatives?.length > 0 && (
                  <div>
                    <span className="font-medium text-indigo-900">Alternatives:</span>
                    <span className="text-indigo-800 ml-2">
                      {data.site_architecture.positioning_inputs.alternatives.join(', ')}
                    </span>
                  </div>
                )}
                <div className="md:col-span-2">
                  <span className="font-medium text-indigo-900">Value Proposition:</span>
                  <span className="text-indigo-800 ml-2">
                    {data.site_architecture.positioning_inputs.value_proposition}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <span className="font-medium text-indigo-900">Disqualifier:</span>
                  <span className="text-indigo-800 ml-2">
                    {data.site_architecture.positioning_inputs.disqualifier}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Phase 2: Keyword Clusters */}
          {data.site_architecture.keyword_clusters &&
            data.site_architecture.keyword_clusters.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                <h2 className="text-lg font-bold text-amber-900">Phase 2: Keyword Clusters</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-amber-300">
                        <th className="text-left py-2 px-2 font-medium text-amber-900">Cluster</th>
                        <th className="text-left py-2 px-2 font-medium text-amber-900">Intent</th>
                        <th className="text-left py-2 px-2 font-medium text-amber-900">Funnel</th>
                        <th className="text-left py-2 px-2 font-medium text-amber-900">ICP</th>
                        <th className="text-left py-2 px-2 font-medium text-amber-900">Page</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.site_architecture.keyword_clusters.map(
                        (cluster: any, idx: number) => (
                          <tr key={idx} className="border-b border-amber-100">
                            <td className="py-2 px-2 text-amber-800">
                              <div className="font-medium">{cluster.cluster_name}</div>
                              <div className="text-xs text-amber-600">
                                {cluster.keywords?.slice(0, 3).join(', ')}
                                {cluster.keywords?.length > 3 && '...'}
                              </div>
                            </td>
                            <td className="py-2 px-2 text-amber-800">{cluster.intent_type}</td>
                            <td className="py-2 px-2 text-amber-800">
                              {cluster.funnel_stage?.replace('_', ' ')}
                            </td>
                            <td className="py-2 px-2 text-amber-800">{cluster.icp_relevance}</td>
                            <td className="py-2 px-2 text-amber-800 font-mono text-xs">
                              {cluster.assigned_page}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          {/* Phase 3-4: Page Types & URL Hierarchy */}
          {data.site_architecture.pages && data.site_architecture.pages.length > 0 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900">Phase 3-4: Page Types & URL Hierarchy</h2>

              {data.site_architecture.pages.map((page: any, pageIndex: number) => (
                <div key={pageIndex} className="border-l-4 border-teal-500 pl-6 space-y-4 pb-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{page.page_name}</h3>
                      <div className="text-sm text-teal-600 font-mono">{page.url_slug}</div>
                      <div className="flex gap-2 mt-1">
                        {page.page_type && (
                          <span className="bg-teal-100 text-teal-800 px-2 py-0.5 rounded text-xs">
                            {page.page_type === 'industry' ? 'solution' : page.page_type}
                          </span>
                        )}
                        {page.parent_page && (
                          <span className="text-xs text-gray-500">
                            Parent: <span className="font-mono">{page.parent_page}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700 italic">{page.page_purpose}</p>

                  {/* Content Hierarchy — supports both old (nested content_hierarchy) and new (flat) formats */}
                  {(() => {
                    const h1 = page.h1 || page.content_hierarchy?.h1;
                    const isOldFormat = page.content_hierarchy && !page.h1;
                    const h2Sections = isOldFormat
                      ? page.content_hierarchy.h2_sections
                      : page.h2_sections;
                    const primaryCta = page.primary_cta;

                    if (!h1 && !h2Sections) return null;

                    return (
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <div className="text-sm font-semibold text-gray-900">Content Hierarchy</div>
                        <div className="space-y-2">
                          {h1 && (
                            <div className="text-xl font-bold text-gray-900">H1: {h1}</div>
                          )}

                          {h2Sections && h2Sections.length > 0 && (
                            <div className="space-y-2 ml-4">
                              {h2Sections.map((h2Item: any, h2Index: number) => {
                                // Old format: h2Item is an object with .h2, .h3_subsections, .key_messaging, .cta
                                // New format: h2Item is a string
                                const isObject = typeof h2Item === 'object';
                                const h2Text = isObject ? h2Item.h2 : h2Item;

                                return (
                                  <div key={h2Index} className="space-y-1">
                                    <div className="text-lg font-semibold text-gray-800">
                                      H2: {h2Text}
                                    </div>

                                    {isObject && h2Item.h3_subsections && h2Item.h3_subsections.length > 0 && (
                                      <div className="ml-4 space-y-1">
                                        {h2Item.h3_subsections.map((h3: string, h3Index: number) => (
                                          <div key={h3Index} className="text-xs text-gray-600">
                                            H3: {h3}
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {isObject && h2Item.key_messaging && (
                                      <div className="text-sm text-gray-600 ml-4">
                                        <span className="font-medium">Messaging:</span>{' '}
                                        {h2Item.key_messaging}
                                      </div>
                                    )}

                                    {isObject && h2Item.cta && (
                                      <div className="text-sm text-purple-600 ml-4">
                                        <span className="font-medium">CTA:</span> {h2Item.cta}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {primaryCta && (
                            <div className="text-sm text-purple-600 mt-2">
                              <span className="font-medium">CTA:</span> {primaryCta}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* SEO Keywords */}
                  {page.seo_keywords && page.seo_keywords.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs font-medium text-gray-600">SEO Keywords:</span>
                      {page.seo_keywords.map((keyword: string, kwIndex: number) => (
                        <span
                          key={kwIndex}
                          className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Phase 5: Navigation Structure */}
          {data.site_architecture.navigation_structure && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-3">
              <h2 className="text-lg font-bold text-teal-900">Phase 5: Navigation Structure</h2>

              {data.site_architecture.navigation_structure.primary_nav && (
                <div>
                  <div className="text-sm font-medium text-teal-900 mb-1">Primary Navigation:</div>
                  <div className="flex flex-wrap gap-2">
                    {data.site_architecture.navigation_structure.primary_nav.map(
                      (item: string, i: number) => (
                        <span
                          key={i}
                          className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm"
                        >
                          {item}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              )}

              {data.site_architecture.navigation_structure.secondary_nav && (
                <div>
                  <div className="text-sm font-medium text-teal-900 mb-1">
                    Secondary Navigation:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {data.site_architecture.navigation_structure.secondary_nav.map(
                      (item: string, i: number) => (
                        <span
                          key={i}
                          className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm"
                        >
                          {item}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              )}

              {data.site_architecture.navigation_structure.footer_nav && (
                <div>
                  <div className="text-sm font-medium text-teal-900 mb-1">Footer Navigation:</div>
                  <div className="flex flex-wrap gap-2">
                    {data.site_architecture.navigation_structure.footer_nav.map(
                      (item: string, i: number) => (
                        <span
                          key={i}
                          className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm"
                        >
                          {item}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Phase 6: Internal Linking Rules */}
          {data.site_architecture.internal_linking_rules &&
            Object.keys(data.site_architecture.internal_linking_rules).length > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                <h2 className="text-lg font-bold text-purple-900">
                  Phase 6: Internal Linking Rules
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {Object.entries(data.site_architecture.internal_linking_rules).map(
                    ([pageType, rules]: [string, any]) => (
                      <div key={pageType} className="bg-white rounded p-2 border border-purple-100">
                        <div className="font-medium text-purple-900 mb-1">
                          {pageType.replace('_', ' ')}
                        </div>
                        <ul className="list-disc list-inside text-purple-700 text-xs">
                          {rules.map((rule: string, idx: number) => (
                            <li key={idx}>{rule}</li>
                          ))}
                        </ul>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}

          {/* Sitemap */}
          {data.site_architecture.sitemap && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Sitemap</h2>
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                {data.site_architecture.sitemap}
              </pre>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Blog Post Rendering
  if (isBlogPost) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Page Type Badge */}
        <div className="bg-blue-100 px-4 py-2 border-b border-blue-200">
          <span className="text-xs font-semibold text-blue-800 uppercase tracking-wide">
            📝 Blog Post
          </span>
        </div>

        <div className="p-6 space-y-6">
          {/* SEO Metadata */}
          {(data.meta_title?.trim() || data.meta_description?.trim()) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
              <div className="text-xs font-semibold text-blue-900 uppercase tracking-wide mb-2">
                SEO Metadata
              </div>
              <div className="space-y-1">
                {data.meta_title?.trim() && (
                  <div className="text-sm">
                    <span className="font-medium text-blue-900">Meta Title:</span>
                    <span className="text-blue-800 ml-2">{data.meta_title}</span>
                    <span className="text-xs text-blue-600 ml-2">
                      ({Math.min(data.meta_title?.length || 0, 60)}/60)
                    </span>
                  </div>
                )}
                {data.meta_description?.trim() && (
                  <div className="text-sm">
                    <span className="font-medium text-blue-900">Meta Description:</span>
                    <span className="text-blue-800 ml-2">{data.meta_description}</span>
                    <span className="text-xs text-blue-600 ml-2">
                      ({Math.min(data.meta_description?.length || 0, 160)}/160)
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Article Title */}
          <div className="border-b border-gray-200 pb-4">
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">{data.hero.h1}</h1>
          </div>

          {/* Article Content */}
          <article className="prose prose-lg max-w-none">
            {data.sections.map((section: any, sectionIndex: number) => (
              <div key={sectionIndex} className="mb-8">
                {section.heading && section.heading !== 'Introduction' && (
                  <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">{section.heading}</h2>
                )}
                {section.subsections && section.subsections.length > 0 && (
                  <div className="space-y-6">
                    {section.subsections.map((subsection: any, subIndex: number) => (
                      <div key={subIndex}>
                        {subsection.subheading && (
                          <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">
                            {subsection.subheading}
                          </h3>
                        )}
                        <div className="text-gray-700 leading-relaxed text-base prose prose-sm max-w-none">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {subsection.content}
                          </ReactMarkdown>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </article>

          {/* Final CTA */}
          {data.cta_final && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-lg text-gray-700 leading-relaxed">{data.cta_final}</p>
            </div>
          )}

          {/* SEO Suggestions */}
          {((data.internal_links && data.internal_links.length > 0) ||
            (data.image_alt_texts && data.image_alt_texts.length > 0)) && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3 mt-6">
              <div className="text-xs font-semibold text-green-900 uppercase tracking-wide">
                SEO Suggestions
              </div>

              {data.internal_links && data.internal_links.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-green-900 mb-1">Internal Links:</div>
                  <ul className="list-disc list-inside text-sm text-green-800 space-y-1">
                    {data.internal_links.map((link: string, linkIndex: number) => (
                      <li key={linkIndex}>{link}</li>
                    ))}
                  </ul>
                </div>
              )}

              {data.image_alt_texts && data.image_alt_texts.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-green-900 mb-1">Image Alt Texts:</div>
                  <ul className="list-disc list-inside text-sm text-green-800 space-y-1">
                    {data.image_alt_texts.map((alt: string, altIndex: number) => (
                      <li key={altIndex}>{alt}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* SEO Score Hint */}
          <p className="text-sm text-gray-700 mt-3">
            Your content is SEO-optimized. You can ask me to check the score to see if needed.
          </p>
        </div>
      </div>
    );
  }

  // Landing Page Rendering (default)
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Page Type Badge */}
      {data.page_type && (
        <div className="bg-purple-100 px-4 py-2 border-b border-purple-200">
          <span className="text-xs font-semibold text-purple-800 uppercase tracking-wide">
            {isLandingPage ? '🚀 Landing Page (Full)' : `${data.page_type} Page`}
          </span>
        </div>
      )}

      <div className="p-6 space-y-8">
        {/* SEO Metadata */}
        {(data.meta_title?.trim() || data.meta_description?.trim()) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
            <div className="text-xs font-semibold text-blue-900 uppercase tracking-wide mb-2">
              SEO Metadata
            </div>
            <div className="space-y-1">
              {data.meta_title?.trim() && (
                <div className="text-sm">
                  <span className="font-medium text-blue-900">Meta Title:</span>
                  <span className="text-blue-800 ml-2">{data.meta_title}</span>
                  <span className="text-xs text-blue-600 ml-2">
                    ({Math.min(data.meta_title?.length || 0, 60)}/60)
                  </span>
                </div>
              )}
              {data.meta_description?.trim() && (
                <div className="text-sm">
                  <span className="font-medium text-blue-900">Meta Description:</span>
                  <span className="text-blue-800 ml-2">{data.meta_description}</span>
                  <span className="text-xs text-blue-600 ml-2">
                    ({Math.min(data.meta_description?.length || 0, 160)}/160)
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SEO Keywords (for landing_page) */}
        {isLandingPage && data.seo_keywords && data.seo_keywords.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-xs font-semibold text-green-900 uppercase tracking-wide mb-2">
              SEO Keywords
            </div>
            <div className="flex flex-wrap gap-2">
              {data.seo_keywords.map((keyword: string, kwIndex: number) => (
                <span
                  key={kwIndex}
                  className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Hero Section */}
        {data.hero && (
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-6 space-y-4">
            <div className="text-xs font-semibold text-purple-900 uppercase tracking-wide">
              Hero Section
            </div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">{data.hero.h1}</h1>
            {data.hero.h2 && (
              <h2 className="text-sm text-gray-700 leading-relaxed">{data.hero.h2}</h2>
            )}
            {data.hero.visual_placeholder && (
              <div className="text-sm text-purple-700 italic">
                Visual: {data.hero.visual_placeholder}
              </div>
            )}
            <div className="flex gap-3">
              {data.hero.primary_cta && (
                <button className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition-colors">
                  {data.hero.primary_cta}
                </button>
              )}
              {data.hero.secondary_cta && (
                <button className="bg-white hover:bg-gray-50 text-purple-600 font-semibold px-6 py-3 rounded-lg border-2 border-purple-300 transition-colors">
                  {data.hero.secondary_cta}
                </button>
              )}
              {/* Fallback for old 'cta' field */}
              {!data.hero.primary_cta && !data.hero.secondary_cta && data.hero.cta && (
                <button className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition-colors">
                  {data.hero.cta}
                </button>
              )}
            </div>
          </div>
        )}

        {/* CTA Variations (for landing_page) */}
        {isLandingPage && data.cta_variations && data.cta_variations.length > 0 && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="text-xs font-semibold text-purple-900 uppercase tracking-wide mb-2">
              CTA Variations (A/B Testing)
            </div>
            <div className="space-y-2">
              {data.cta_variations.map((cta: string, ctaIndex: number) => (
                <div
                  key={ctaIndex}
                  className="bg-white px-4 py-2 rounded border border-purple-200 text-sm"
                >
                  {ctaIndex + 1}. {cta}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Content Sections */}
        {data.sections && data.sections.length > 0 && (
          <div className="space-y-6">
            {data.sections.map((section: any, sectionIndex: number) => (
              <div key={sectionIndex} className="border-l-4 border-indigo-500 pl-6 space-y-4">
                <h2 className="text-xl font-bold text-gray-900">{section.heading}</h2>
                {section.subsections && section.subsections.length > 0 && (
                  <div className="space-y-4">
                    {section.subsections.map((subsection: any, subIndex: number) => (
                      <div key={subIndex} className="space-y-2">
                        {subsection.subheading && (
                          <h3 className="text-lg font-semibold text-gray-800">
                            {subsection.subheading}
                          </h3>
                        )}
                        <div className="text-gray-700 leading-relaxed text-base prose prose-sm max-w-none prose-table:border-collapse prose-td:border prose-td:border-gray-300 prose-td:px-3 prose-td:py-2 prose-th:border prose-th:border-gray-300 prose-th:px-3 prose-th:py-2 prose-th:bg-gray-50">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {subsection.content}
                          </ReactMarkdown>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Final CTA */}
        {data.cta_final && (
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-6 text-center shadow-lg">
            <p className="text-sm font-bold">{data.cta_final}</p>
          </div>
        )}

        {/* SEO Suggestions */}
        {((data.internal_links && data.internal_links.length > 0) ||
          (data.image_alt_texts && data.image_alt_texts.length > 0)) && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
            <div className="text-xs font-semibold text-green-900 uppercase tracking-wide">
              SEO Suggestions
            </div>

            {data.internal_links && data.internal_links.length > 0 && (
              <div>
                <div className="text-sm font-medium text-green-900 mb-1">Internal Links:</div>
                <ul className="list-disc list-inside text-sm text-green-800 space-y-1">
                  {data.internal_links.map((link: string, linkIndex: number) => (
                    <li key={linkIndex}>{link}</li>
                  ))}
                </ul>
              </div>
            )}

            {data.image_alt_texts && data.image_alt_texts.length > 0 && (
              <div>
                <div className="text-sm font-medium text-green-900 mb-1">Image Alt Texts:</div>
                <ul className="list-disc list-inside text-sm text-green-800 space-y-1">
                  {data.image_alt_texts.map((alt: string, altIndex: number) => (
                    <li key={altIndex}>{alt}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* SEO Score Hint */}
        <p className="text-sm text-gray-700 mt-3">
          Your content is SEO-optimized. You can ask me to check the score to see if needed.
        </p>
      </div>
    </div>
  );
};

export default MarketingStrategistRenderer;
